import { Link } from "react-router-dom";
import { ArrowRight, Users, Layers, Clock3, ShieldCheck, Megaphone, Handshake, Package, MonitorSmartphone, Mail, Phone } from "lucide-react";
import { Reveal, MaskedLine, Kicker } from "@/components/Reveal";
import WhereToWatch from "@/components/WhereToWatch";
import { CABLE_OPERATORS, CONTACT } from "@/data/content";

const WHY = [
    { icon: Users, title: "Massive Reach", text: "Access 2.5 crore+ households across West Bengal, Tripura, Assam and Eastern India through 25+ cable and DTH operators." },
    { icon: Layers, title: "Diverse Demographics", text: "As a GEC, our programming attracts viewers across all age groups — children, youth, homemakers and families." },
    { icon: Clock3, title: "Premium Time Slots", text: "From morning lifestyle shows to prime-time entertainment, choose the slot that best aligns with your audience." },
    { icon: ShieldCheck, title: "Trusted Platform", text: "Built on the legacy of India's first Bengali Kids Channel, Rongeen TV enjoys deep trust among Bengali audiences." },
];

const OPTIONS = [
    { icon: Megaphone, title: "Spot Advertising", text: "Traditional commercial slots during shows across the day." },
    { icon: Handshake, title: "Sponsorship", text: "Show sponsorship and title sponsorship opportunities." },
    { icon: Package, title: "Brand Integration", text: "In-show product placement and seamless brand integration." },
    { icon: MonitorSmartphone, title: "Digital Cross-Promotion", text: "Combined TV + digital advertising packages." },
];

export default function Advertise() {
    return (
        <div data-testid="advertise-page">
            <section className="noise-overlay relative overflow-hidden bg-gradient-to-br from-[#FFA200] via-[#C8102E] to-[#8F0A20] pb-20 pt-36 sm:pb-24 sm:pt-44">
                <div className="absolute -right-24 top-0 h-96 w-96 rounded-full bg-white/10 blur-[100px]" />
                <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
                    <Reveal>
                        <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-white/90">
                            <span className="h-[2px] w-6 bg-current" /> Advertise With Us
                        </span>
                    </Reveal>
                    <h1 className="font-display mt-5 text-4xl font-black tracking-tight text-white sm:text-5xl lg:text-6xl">
                        <MaskedLine delay={0.1}>Connect your brand with</MaskedLine>
                        <MaskedLine delay={0.22}>millions of Bengali homes</MaskedLine>
                    </h1>
                    <Reveal delay={0.35}>
                        <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/85 sm:text-lg">
                            Eastern India's most-watched entertainment destination — and your brand's loudest stage.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="bg-[#0F0F23] py-20 sm:py-28" data-testid="why-advertise-section">
                <div className="mx-auto max-w-7xl px-4 sm:px-6">
                    <Reveal>
                        <Kicker>Why Rongeen TV</Kicker>
                        <h2 className="font-display mt-4 text-3xl font-black tracking-tight text-white sm:text-4xl">
                            Four reasons your brand belongs here
                        </h2>
                    </Reveal>
                    <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                        {WHY.map((w, i) => {
                            const Icon = w.icon;
                            return (
                                <Reveal key={w.title} delay={i * 0.08}>
                                    <div data-testid={`why-card-${i}`} className="group h-full rounded-3xl border border-white/10 bg-[#16162B] p-7 transition-all duration-500 hover:-translate-y-2 hover:border-[#C8102E]/50">
                                        <div className="inline-flex rounded-2xl bg-[#C8102E]/15 p-3 text-[#C8102E] transition-colors group-hover:bg-[#C8102E] group-hover:text-white">
                                            <Icon size={22} />
                                        </div>
                                        <h3 className="font-display mt-5 text-lg font-black text-white">{w.title}</h3>
                                        <p className="mt-2 text-sm leading-relaxed text-[#9999BB]">{w.text}</p>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>
                    <Reveal delay={0.15}>
                        <div className="gold-border-glow mt-12 rounded-3xl bg-[#16162B] p-8 sm:p-10" data-testid="competitive-edge">
                            <Kicker>Stand Out</Kicker>
                            <p className="mt-4 max-w-4xl text-base leading-relaxed text-[#C9C9DE] sm:text-lg">
                                In a market dominated by players like Colors Bangla and Sony Aath, Rongeen TV offers advertisers a unique proposition — a channel built on a decade of trust with Bengali families through children's programming, now expanded into a full-spectrum GEC. Our loyal viewer base, combined with fresh entertainment content, delivers an engaged audience that competing channels can't replicate.
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="bg-[#F5F5F5] py-20 sm:py-28" data-testid="ad-options-section">
                <div className="mx-auto max-w-7xl px-4 sm:px-6">
                    <Reveal>
                        <Kicker light>Advertising Options</Kicker>
                        <h2 className="font-display mt-4 text-3xl font-black tracking-tight text-[#0F0F23] sm:text-4xl">
                            Formats that fit your goals
                        </h2>
                    </Reveal>
                    <div className="mt-12 grid gap-5 sm:grid-cols-2">
                        {OPTIONS.map((o, i) => {
                            const Icon = o.icon;
                            return (
                                <Reveal key={o.title} delay={i * 0.08}>
                                    <div data-testid={`ad-option-${i}`} className="flex h-full items-start gap-5 rounded-3xl border border-black/10 bg-white p-7 transition-all duration-300 hover:border-[#C8102E]/40 hover:shadow-xl hover:shadow-[#C8102E]/10">
                                        <div className="shrink-0 rounded-2xl bg-[#C8102E]/10 p-3 text-[#C8102E]">
                                            <Icon size={22} />
                                        </div>
                                        <div>
                                            <h3 className="font-display text-lg font-black text-[#0F0F23]">{o.title}</h3>
                                            <p className="mt-1.5 text-sm leading-relaxed text-[#555566]">{o.text}</p>
                                        </div>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>

                    <Reveal delay={0.15}>
                        <div className="mt-14 rounded-3xl bg-[#0F0F23] p-8 sm:p-10" data-testid="rate-card-cta">
                            <div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
                                <div>
                                    <h3 className="font-display text-2xl font-black text-white sm:text-3xl">Request our media kit</h3>
                                    <p className="mt-3 max-w-xl text-sm leading-relaxed text-[#9999BB]">
                                        For detailed rate cards, media kits and customized advertising solutions, get in touch with our sales team.
                                    </p>
                                    <div className="mt-5 flex flex-wrap gap-x-8 gap-y-2 text-sm">
                                        <a href={`mailto:${CONTACT.email}`} data-testid="advertise-email-link" className="inline-flex items-center gap-2 text-[#FFA200] transition-colors hover:text-white">
                                            <Mail size={15} /> {CONTACT.email}
                                        </a>
                                        <a href={`tel:${CONTACT.phoneHref}`} data-testid="advertise-phone-link" className="inline-flex items-center gap-2 text-[#FFA200] transition-colors hover:text-white">
                                            <Phone size={15} /> {CONTACT.phone}
                                        </a>
                                    </div>
                                </div>
                                <Link
                                    to="/contact"
                                    data-testid="contact-sales-btn"
                                    className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-[#C8102E] px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] text-white transition-all duration-300 hover:bg-[#8F0A20]"
                                >
                                    Contact Sales <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                                </Link>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            <WhereToWatch dark />

            <section className="bg-[#0F0F23] pb-24 sm:pb-32" data-testid="cable-network-section">
                <div className="mx-auto max-w-7xl px-4 sm:px-6">
                    <Reveal>
                        <Kicker>Our Distribution Network</Kicker>
                        <h2 className="font-display mt-4 text-3xl font-black tracking-tight text-white sm:text-4xl">
                            Regional cable operators
                        </h2>
                    </Reveal>
                    <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                        {CABLE_OPERATORS.map(([name, lcn], i) => (
                            <Reveal key={name} delay={(i % 5) * 0.05}>
                                <div data-testid={`cable-op-${i}`} className="rounded-2xl border border-white/10 bg-[#16162B] px-4 py-4 transition-colors hover:border-[#C8102E]/50">
                                    <p className="truncate text-[11px] font-bold uppercase tracking-[0.12em] text-[#9999BB]">{name}</p>
                                    <p className="font-mono2 mt-1 text-xl font-black text-white">
                                        {lcn} <span className="text-[10px] font-semibold uppercase tracking-widest text-[#C8102E]">LCN</span>
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}
